import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TalkingPointComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point/talking-point.component';
import {
  IKeywordCard,
  IKpiCard,
} from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { ITalkingPointAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { ITalkingPointStreamAnalyticsTableData } from './talking-point-stream-analytics.interface';

@Component({
  imports: [
    DatePipe,
    TranslateModule,
    LineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    EmbeddedVideoPlayerComponent,
    TalkingPointComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
    MatMenuModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-talking-point-stream-analytics',
  templateUrl: './talking-point-stream-analytics.component.html',
})
export class TalkingPointStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  ITalkingPointStreamAnalyticsTableData,
  ITalkingPointAnalytics
> {
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'talkingPoint-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.totalCount'),
    value: 0,
  };

  public override readonly displayedColumns: string[] = ['timestamp', 'videoTime', 'title', 'actions'];
  public override readonly dataSource: MatTableDataSource<ITalkingPointStreamAnalyticsTableData> =
    new MatTableDataSource();

  public readonly topicKpiList = signal<IKeywordCard[]>([]);
  public readonly topicKpiCardList = signal<IKpiCard[]>([]);
  public readonly allKpiCards = computed(() => [this.kpiCardData, ...this.topicKpiCardList()]);
  public override readonly deliverableType = EDeliverableType.talkingPoint;

  protected override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.talkingPoints?.find((tp) => tp.deliverableId === deliverableId);
    }

    return null;
  });

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();
        this.setStatus();
        this.setStreamData();
        this.setKeywordList();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.timelineData()) {
        this.prepareChartAndTableData();
      }
    });

    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        const talkingPoint = this.currentDeliverable();

        this.keywords = talkingPoint?.keywords?.map((topic) => topic.keyword) ?? [];
      }
    });
  }

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  protected override prepareChartAndTableData(): void {
    const dotGraphData: ILineGraphInputData[] = [];
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const timelineData = this.timelineData() ?? [];
    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: ITalkingPointStreamAnalyticsTableData[] = visualizers.map((visualizer) => {
      const [primaryFinding] = visualizer.findings;

      const kwAnalytics = keywords.find((k) => k.keywordId === primaryFinding.parentId);
      const entryTimestamp = this.dateUtils.parseDateTimeFromApi(primaryFinding.timestamp).getTime();
      const interpolatedViewCount = this.graphUtilities.getInterpolatedViewCount(sortedDictionary, entryTimestamp);

      dotGraphData.push({
        id: visualizer.id,
        status: kwAnalytics?.status ?? primaryFinding.status,
        xAxisValue: entryTimestamp,
        yAxisValue: Math.round(interpolatedViewCount),
      });

      return {
        ...visualizer,
        text: primaryFinding.text,
        timestamp: primaryFinding.timestamp,
        videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(primaryFinding.videoTime),
        videoTimeSeconds: primaryFinding.videoTime ?? 0,
      };
    });

    const sortedTableData = [...tableData].sort(
      (a, b) =>
        this.dateUtils.parseDateTimeFromApi(a.timestamp).getTime() -
        this.dateUtils.parseDateTimeFromApi(b.timestamp).getTime(),
    );

    this.dataSource.data = sortedTableData;
    this.dataCount.set(sortedTableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
    this.setKeywordTableRowIds(this.findingVisualizerService.buildKeywordRowIdMap(sortedTableData));
  }

  protected override setCounts(): void {
    const talkingPoint = this.currentDeliverable();
    const topics = talkingPoint?.keywords ?? [];
    const topicNames = topics.map((t) => t.keyword);
    const findings = topics.flatMap((t) => t.findings ?? []);
    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(findings) as string[];

    const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(topicNames, uniqueFindings);
    const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), counts);
  }

  protected override setKeywordList(): void {
    const talkingPoint = this.currentDeliverable();

    if (talkingPoint?.keywords && talkingPoint.keywords.length > 0) {
      const { keywordList, kpiCardList } = AnalyticsDetailsUtilitiesService.buildKeywordLists(
        talkingPoint.keywords,
        'keyword',
        'findings',
      );
      this.topicKpiList.set(keywordList);
      this.topicKpiCardList.set(kpiCardList);
    }
  }
}
